import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/ui/organisms/PageHeader';
import Eyebrow from '@/ui/atoms/Eyebrow';
import Button from '@/ui/atoms/Button';
import { CouncilPreview } from '@/components/CouncilPreview';
import { TopicChipPicker } from '@/components/TopicChipPicker';
import { useAhaFlag } from '@/hooks/useAhaFlag';
import { useOnboarding } from '@/store/onboarding';
import { useFydUser } from '@/store/auth';
import { sessionCreate } from '@/api/m0_sessions';
import { ApiError } from '@/api/client';
import { fireAhaEvent } from '@/lib/analytics';
import { SAMPLE_CHIPS, chipForIntentPreset, type SampleChip } from '@/lib/fixtures/sample_chips';

// /onboarding — 2 paths:
//   FYD_AHA_ONBOARDING_2STEP=1 → P1 streamlined preview + pick_topic.
//   else → legacy F-16 3-card intro flow (preserved verbatim).

interface CardSpec {
  id: 'vertical_lock' | 'three_rules' | 'depth_lock';
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  cta: string;
}

export default function Onboarding() {
  const twoStepEnabled = useAhaFlag('FYD_AHA_ONBOARDING_2STEP');
  if (twoStepEnabled) return <OnboardingTwoStep />;
  return <OnboardingLegacy />;
}

function OnboardingTwoStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useFydUser((s) => s.me);
  const step = useOnboarding((s) => s.step);
  const setStep = useOnboarding((s) => s.setStep);
  const recordMount = useOnboarding((s) => s.recordMount);
  const mountedAt = useOnboarding((s) => s.mountedAt);
  const selectChip = useOnboarding((s) => s.selectChip);
  const setCustomTopic = useOnboarding((s) => s.setCustomTopic);
  const errorCode = useOnboarding((s) => s.errorCode);
  const setError = useOnboarding((s) => s.setError);
  const [busy, setBusy] = useState(false);

  const defaultChip = useMemo<SampleChip | undefined>(() => {
    if (!me?.last_intent_preset) return undefined;
    return chipForIntentPreset(me.last_intent_preset);
  }, [me?.last_intent_preset]);

  useEffect(() => {
    if (!mountedAt) recordMount();
  }, [mountedAt, recordMount]);

  // Returning users skip onboarding entirely.
  useEffect(() => {
    if (me && me.councils_run > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [me, navigate]);

  const launchSession = async (topic: string, intent: string | null, chipId?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await sessionCreate({
        topic,
        ...(intent && intent !== 'auto' ? { intent_preset: intent as never } : {}),
      });
      const ttfv = mountedAt ? Date.now() - mountedAt : 0;
      fireAhaEvent('first_council_launched', {
        user_id: 'auth',
        ttfv_ms_from_signup: ttfv,
        intent_preset: intent ?? 'auto',
        topic_source: chipId ? 'chip' : 'custom',
      });
      if (chipId) {
        fireAhaEvent('first_value.first_run.try_sample_topic', {
          user_id: 'auth',
          category_id: 'first_run',
          click_to_value_steps: 1,
          ttfv_ms_from_screen_mount: ttfv,
          intent_preset: intent ?? 'auto',
          topic_source: 'chip',
          sample_chip_id: chipId,
        });
      }
      setStep('launched');
      navigate(`/sessions/${r.session_id}/live`, { state: { sseUrl: r.sse_url } });
    } catch (err) {
      if (err instanceof ApiError) setError(err.errorCode);
      else setError('unknown');
    } finally {
      setBusy(false);
    }
  };

  const onChip = (chip: SampleChip) => {
    selectChip(chip);
    void launchSession(chip.topic_template, chip.intent_preset, chip.chip_id);
  };

  const onCustomSubmit = (topic: string) => {
    setCustomTopic(topic);
    void launchSession(topic, 'auto');
  };

  return (
    <div className="max-w-3xl mx-auto px-8 py-16" data-testid="onboarding-2step">
      <PageHeader
        eyebrow={t('nav.howItWorks')}
        title={
          step === 'preview'
            ? t('ahaOnboarding.preview.title')
            : t('ahaOnboarding.pickTopic.title')
        }
      />

      {step === 'preview' && (
        <CouncilPreview
          onComplete={() => setStep('pick_topic')}
          onSkip={() => setStep('pick_topic')}
        />
      )}

      {step !== 'preview' && (
        <>
          <TopicChipPicker
            chips={SAMPLE_CHIPS}
            defaultChipId={defaultChip?.chip_id}
            onChipSelected={onChip}
            onCustomTopicChange={setCustomTopic}
            onCustomTopicSubmit={onCustomSubmit}
            busy={busy}
          />
          {errorCode && (
            <p className="text-vermilion text-sm mt-4" data-testid="onboarding-error">
              error · {errorCode}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function OnboardingLegacy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const cards: CardSpec[] = [
    {
      id: 'vertical_lock',
      eyebrow: t('onboarding.f16.card1.eyebrow'),
      title: t('onboarding.f16.card1.title'),
      body: t('onboarding.f16.card1.body'),
      bullets: [
        t('onboarding.f16.card1.bullet1'),
        t('onboarding.f16.card1.bullet2'),
        t('onboarding.f16.card1.bullet3'),
      ],
      cta: t('common.next'),
    },
    {
      id: 'three_rules',
      eyebrow: t('onboarding.f16.card2.eyebrow'),
      title: t('onboarding.f16.card2.title'),
      body: t('onboarding.f16.card2.body'),
      bullets: [
        t('onboarding.f16.card2.rule1'),
        t('onboarding.f16.card2.rule2'),
        t('onboarding.f16.card2.rule3'),
      ],
      cta: t('common.next'),
    },
    {
      id: 'depth_lock',
      eyebrow: t('onboarding.f16.card3.eyebrow'),
      title: t('onboarding.f16.card3.title'),
      body: t('onboarding.f16.card3.body'),
      bullets: [
        t('onboarding.f16.card3.bullet1'),
        t('onboarding.f16.card3.bullet2'),
        t('onboarding.f16.card3.bullet3'),
      ],
      cta: t('onboarding.f16.startCouncil'),
    },
  ];

  const card = cards[step];
  const isLast = step === cards.length - 1;

  const onAdvance = () => {
    if (isLast) {
      navigate('/dashboard');
      return;
    }
    setStep((s) => s + 1);
  };

  return (
    <div className="max-w-3xl mx-auto px-8 py-16" data-testid="onboarding-root">
      <PageHeader
        eyebrow={t('onboarding.f16.eyebrow')}
        title={t('onboarding.f16.title')}
        sub={t('onboarding.f16.sub')}
      />

      <div className="flex justify-center gap-2 mb-10" data-testid="onboarding-progress">
        {cards.map((c, i) => (
          <span
            key={c.id}
            className={`h-2 rounded-full transition-all ${
              i === step
                ? 'w-8 bg-vermilion'
                : i < step
                  ? 'w-4 bg-jade'
                  : 'w-4 bg-hairline'
            }`}
            aria-hidden
          />
        ))}
      </div>

      <article
        key={card.id}
        className="px-10 py-12 bg-paper border border-hairline rounded-md mb-8"
        data-testid={`onboarding-card-${card.id}`}
      >
        <Eyebrow tone={step === 1 ? 'vermilion' : step === 2 ? 'gold' : 'azure'} className="mb-4">
          {card.eyebrow}
        </Eyebrow>
        <h2 className="font-serif font-medium text-3xl mb-4">{card.title}</h2>
        <p className="text-ink-2 leading-[1.7] text-[16px] mb-6">{card.body}</p>
        <ul className="space-y-3">
          {card.bullets.map((b, i) => (
            <li key={i} className="text-ink relative pl-6 leading-[1.7]">
              <span className="absolute left-0 top-[12px] w-3 h-px bg-jade" />
              {b}
            </li>
          ))}
        </ul>
      </article>

      <div className="flex justify-between items-center">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="onboarding-back"
        >
          {t('common.back')}
        </Button>
        <span className="font-mono text-[10px] tracking-meta uppercase text-ink-3">
          {step + 1} / {cards.length}
        </span>
        <Button
          variant={isLast ? 'vermilion' : 'primary'}
          onClick={onAdvance}
          data-testid="onboarding-next"
        >
          {card.cta}
        </Button>
      </div>
    </div>
  );
}
